/* ==========================================================================
   AtomBiker Global JS
   Core interactivity: header scroll, drawer, modal, toast, cart
   No jQuery. No frameworks. Vanilla JS, deferred loading.
   ========================================================================== */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     1. HEADER — Sticky hide/show on scroll
     -------------------------------------------------------------------------- */
  const Header = {
    el: null,
    lastScroll: 0,
    threshold: 100,

    init() {
      this.el = document.querySelector('.header');
      if (!this.el) return;

      let ticking = false;
      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            this.onScroll();
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    },

    onScroll() {
      const currentScroll = window.scrollY;

      if (currentScroll <= 0) {
        this.el.classList.remove('is-hidden', 'is-scrolled');
        this.lastScroll = currentScroll;
        return;
      }

      if (currentScroll > this.threshold) {
        this.el.classList.add('is-scrolled');
      } else {
        this.el.classList.remove('is-scrolled');
      }

      if (currentScroll > this.lastScroll && currentScroll > this.threshold) {
        // Scrolling down
        this.el.classList.add('is-hidden');
      } else {
        // Scrolling up
        this.el.classList.remove('is-hidden');
      }

      this.lastScroll = currentScroll;
    }
  };

  /* --------------------------------------------------------------------------
     2. DRAWER — Open/Close any drawer
     -------------------------------------------------------------------------- */
  const Drawer = {
    activeDrawer: null,
    backdrop: null,

    init() {
      this.backdrop = document.querySelector('[data-backdrop]');

      // Open triggers
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-drawer-open]');
        if (trigger) {
          e.preventDefault();
          const targetId = trigger.getAttribute('data-drawer-open');
          const drawer = document.querySelector(`[data-${targetId}]`);
          if (drawer) this.open(drawer);
        }
      });

      // Close triggers
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') ||
            e.target.closest('[data-cart-drawer-close]')) {
          // Only preventDefault on non-link elements (buttons, etc.)
          // Let anchor links navigate naturally after closing the drawer
          if (!e.target.closest('a[href]')) {
            e.preventDefault();
          }
          this.close();
        }
      });

      // Backdrop click closes
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this.close());
      }

      // Escape key closes
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.activeDrawer) {
          this.close();
        }
      });
    },

    open(drawer) {
      if (this.activeDrawer) this.close();

      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (this.backdrop) this.backdrop.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      this.activeDrawer = drawer;

      // Focus first focusable element
      const focusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusable) focusable.focus();
    },

    close() {
      if (!this.activeDrawer) return;

      this.activeDrawer.classList.remove('is-open');
      this.activeDrawer.setAttribute('aria-hidden', 'true');
      if (this.backdrop) this.backdrop.classList.remove('is-active');
      document.body.style.overflow = '';
      this.activeDrawer = null;
    }
  };

  /* --------------------------------------------------------------------------
     3. MODAL — Open/Close modals
     -------------------------------------------------------------------------- */
  const Modal = {
    activeModal: null,

    init() {
      document.addEventListener('click', (e) => {
        const openTrigger = e.target.closest('[data-modal-open]');
        if (openTrigger) {
          e.preventDefault();
          const targetId = openTrigger.getAttribute('data-modal-open');
          const modal = document.getElementById(targetId);
          if (modal) this.open(modal);
        }

        const closeTrigger = e.target.closest('[data-modal-close]');
        if (closeTrigger) {
          e.preventDefault();
          this.close();
        }

        // Click backdrop to close
        if (e.target.classList.contains('modal__backdrop')) {
          this.close();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.activeModal) {
          this.close();
        }
      });
    },

    open(modal) {
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.activeModal = modal;

      const focusable = modal.querySelector('.modal__content button, .modal__content [href], .modal__content input');
      if (focusable) focusable.focus();
    },

    close() {
      if (!this.activeModal) return;
      this.activeModal.classList.remove('is-open');
      document.body.style.overflow = '';
      this.activeModal = null;
    }
  };

  /* --------------------------------------------------------------------------
     4. TOAST — Notification system
     -------------------------------------------------------------------------- */
  const Toast = {
    container: null,

    init() {
      this.container = document.querySelector('[data-toast-container]');
    },

    show(message, type = 'default', duration = 4000) {
      if (!this.container) return;

      const toast = document.createElement('div');
      toast.className = `toast toast--${type}`;
      toast.innerHTML = `
        <div class="toast__body">
          <p class="toast__message">${message}</p>
        </div>
        <button class="toast__close" aria-label="Dismiss notification">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
      `;

      toast.querySelector('.toast__close').addEventListener('click', () => {
        this.dismiss(toast);
      });

      this.container.appendChild(toast);

      if (duration > 0) {
        setTimeout(() => this.dismiss(toast), duration);
      }
    },

    dismiss(toast) {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(10px)';
      toast.style.transition = 'opacity 200ms, transform 200ms';
      setTimeout(() => toast.remove(), 200);
    }
  };

  /* --------------------------------------------------------------------------
     5. CART — AJAX cart operations
     -------------------------------------------------------------------------- */
  const Cart = {
    drawer: null,
    drawerBody: null,
    drawerFooter: null,

    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.drawerBody = document.querySelector('[data-cart-drawer-body]');
      this.drawerFooter = document.querySelector('[data-cart-drawer-footer]');

      // Open cart
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cart-open]');
        if (trigger) {
          e.preventDefault();
          this.openDrawer();
        }
      });

      // Handle ATC forms
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action="/cart/add"]');
        if (form && !form.hasAttribute('data-no-ajax')) {
          e.preventDefault();
          this.addItem(form);
        }
      });

      // Delegated quantity stepper for cart drawer
      if (this.drawerBody) {
        this.drawerBody.addEventListener('click', (e) => {
          const qb = e.target.closest('[data-qty-change]');
          if (!qb) return;
          e.preventDefault();
          const key = qb.getAttribute('data-qty-change');
          const dir = parseInt(qb.getAttribute('data-qty-dir'), 10);
          const numEl = qb.parentElement.querySelector('[data-qty-num]');
          const current = parseInt((numEl && numEl.textContent) || '1', 10) || 1;
          this.changeQty(key, Math.max(0, current + dir));
        });
      }
    },

    async addItem(form) {
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.classList.add('is-loading');

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) throw new Error('Could not add to cart');

        const item = await response.json();
        this.updateCartCount();
        this.openDrawer();
      } catch (err) {
        Toast.show('Could not add item to cart. Please try again.', 'error');
      } finally {
        if (submitBtn) submitBtn.classList.remove('is-loading');
      }
    },

    async openDrawer() {
      await this.renderDrawer();
      Drawer.open(this.drawer);
    },

    async renderDrawer() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        if (cart.item_count === 0) {
          this.drawerBody.innerHTML = `
            <div class="empty-state empty-state--cart" style="padding: var(--space-12) var(--space-4)">
              <svg class="empty-state__icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
              <h3 class="empty-state__title">Your cart is empty</h3>
              <p class="empty-state__text">Looks like you haven't found your gear yet.</p>
              <div class="empty-state__actions">
                <a href="/collections/best-sellers" class="btn btn--primary" data-drawer-close>Shop best sellers</a>
              </div>
            </div>
          `;
          this.drawerFooter.innerHTML = '';
          return;
        }

        // Render cart items
        this.drawerBody.innerHTML = cart.items.map(item => `
          <div class="cart-item" data-cart-item data-key="${item.key}">
            <div class="cart-item__image">
              ${item.image ? `<img src="${this.getSizedImageUrl(item.image, '160x200')}" alt="${item.title}" width="80" height="100" loading="lazy">` : ''}
            </div>
            <div class="cart-item__details">
              <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
              ${item.variant_title ? `<span class="cart-item__variant">${item.variant_title}</span>` : ''}
              <div class="cart-item__bottom"><div class="cart-qty"><button type="button" class="cart-qty__btn" data-qty-change="${item.key}" data-qty-dir="-1" aria-label="Decrease quantity">&minus;</button><span class="cart-qty__num" data-qty-num>${item.quantity}</span><button type="button" class="cart-qty__btn" data-qty-change="${item.key}" data-qty-dir="1" aria-label="Increase quantity">+</button></div><span class="cart-item__price">${this.formatMoney(item.final_line_price)}</span></div><div class="cart-item__meta"><span class="cart-item__unit">${this.formatMoney(item.final_price)} each</span><button class="cart-item__remove" data-remove-item="${item.key}" aria-label="Remove ${item.product_title}">Remove</button></div>
            </div>
          </div>
        `).join('');

        // Render footer
        this.drawerFooter.innerHTML = `
          <div class="cart-footer__trust"><span><b></b>Secure checkout</span><span><b></b>Genuine leather</span><span><b></b>Tracked shipping</span></div>
          <div class="cart-footer__totals">
            <span class="cart-footer__total-label">Subtotal</span>
            <span class="cart-footer__total-value">${this.formatMoney(cart.total_price)}</span>
          </div>
          <a href="/checkout" class="btn btn--atc">Checkout &middot; ${this.formatMoney(cart.total_price)}</a>
          <p class="text-caption text-center mt-2" style="color: var(--text-tertiary)">Shipping & taxes calculated at checkout</p>
        `;

        // Bind remove buttons
        this.drawerBody.querySelectorAll('[data-remove-item]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.removeItem(btn.getAttribute('data-remove-item'));
          });
        });
      } catch (err) {
        this.drawerBody.innerHTML = '<p class="p-4 text-center text-secondary">Could not load cart. Please try again.</p>';
      }
    },

    async removeItem(key) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        this.updateCartCount();
        this.renderDrawer();
      } catch (err) {
        Toast.show('Could not remove item. Please try again.', 'error');
      }
    },

    async changeQty(key, quantity) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity }),
        });
        this.updateCartCount();
        this.renderDrawer();
      } catch (err) {
        Toast.show('Could not update quantity. Please try again.', 'error');
      }
    },

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.textContent = cart.item_count || '';
        });
      } catch (err) {
        // Silently fail — non-critical
      }
    },

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    },

    getSizedImageUrl(url, size) {
      if (!url) return '';
      return url.replace(/\.([^.]+)$/, `_${size}.$1`);
    }
  };

  /* --------------------------------------------------------------------------
     6. MOBILE NAV — Expandable sub-menus
     -------------------------------------------------------------------------- */
  const MobileNav = {
    init() {
      document.querySelectorAll('[data-mobile-nav-toggle]').forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          const sub = toggle.nextElementSibling;
          if (sub && sub.classList.contains('mobile-nav__sub')) {
            const isOpen = sub.classList.contains('is-open');
            // Close all open subs
            document.querySelectorAll('.mobile-nav__sub.is-open').forEach(s => s.classList.remove('is-open'));
            // Toggle this one
            if (!isOpen) sub.classList.add('is-open');
            // Rotate chevron
            const chevron = toggle.querySelector('svg');
            if (chevron) {
              chevron.style.transform = isOpen ? '' : 'rotate(90deg)';
            }
          }
        });
      });
    }
  };

  /* --------------------------------------------------------------------------
     7. STICKY ATC — Show on mobile PDP when ATC button scrolls out of view
     -------------------------------------------------------------------------- */
  const StickyATC = {
    init() {
      const stickyBar = document.querySelector('.sticky-atc');
      const atcButton = document.querySelector('[data-atc-trigger]');
      if (!stickyBar || !atcButton) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        },
        { threshold: 0 }
      );

      observer.observe(atcButton);
    }
  };

  /* --------------------------------------------------------------------------
     8. QUANTITY SELECTOR
     -------------------------------------------------------------------------- */
  const Quantity = {
    init() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quantity-btn]');
        if (!btn) return;

        const wrapper = btn.closest('.quantity');
        const input = wrapper.querySelector('.quantity__input');
        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || 99;
        let value = parseInt(input.value) || 1;

        if (btn.getAttribute('data-quantity-btn') === 'minus') {
          value = Math.max(min, value - 1);
        } else {
          value = Math.min(max, value + 1);
        }

        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  };

  /* --------------------------------------------------------------------------
     9. WISHLIST — localStorage-based wishlist toggle
     -------------------------------------------------------------------------- */
  const Wishlist = {
    storageKey: 'atombiker_wishlist',

    init() {
      this.hydrate();
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-wishlist-toggle]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        const productId = btn.getAttribute('data-product-id');
        this.toggle(productId, btn);
      });
    },

    getList() {
      try {
        return JSON.parse(localStorage.getItem(this.storageKey)) || [];
      } catch {
        return [];
      }
    },

    toggle(productId, btn) {
      const list = this.getList();
      const index = list.indexOf(productId);
      if (index > -1) {
        list.splice(index, 1);
        btn.classList.remove('is-wishlisted');
      } else {
        list.push(productId);
        btn.classList.add('is-wishlisted');
      }
      localStorage.setItem(this.storageKey, JSON.stringify(list));
    },

    hydrate() {
      const list = this.getList();
      document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
        const productId = btn.getAttribute('data-product-id');
        if (list.includes(productId)) {
          btn.classList.add('is-wishlisted');
        }
      });
    }
  };

  /* --------------------------------------------------------------------------
     10. PRODUCT TILE SWATCHES — Image swap on swatch click
     -------------------------------------------------------------------------- */
  const TileSwatches = {
    init() {
      document.addEventListener('click', (e) => {
        const swatch = e.target.closest('[data-swatch-trigger]');
        if (!swatch) return;

        const tile = swatch.closest('.product-tile');
        if (!tile) return;

        const mainImg = tile.querySelector('.product-tile__img');
        const newSrc = swatch.dataset.mediaSrc;
        if (!mainImg || !newSrc) return;

        mainImg.src = newSrc;

        tile.querySelectorAll('.product-tile__swatch').forEach(s => s.classList.remove('is-active'));
        swatch.classList.add('is-active');

        e.preventDefault();
        e.stopPropagation();
      });
    }
  };

  /* --------------------------------------------------------------------------
     11. COLLECTION FILTERS — Auto-submit on change, toggle groups
     -------------------------------------------------------------------------- */

  /* --------------------------------------------------------------------------
     12. INIT — Run on DOM ready
     -------------------------------------------------------------------------- */
  function init() {
    Header.init();
    Drawer.init();
    Modal.init();
    Toast.init();
    Cart.init();
    MobileNav.init();
    StickyATC.init();
    Quantity.init();
    Wishlist.init();
    TileSwatches.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose modules globally for use in sections/snippets
  window.AtomBiker = { Toast, Cart, Drawer, Modal, Wishlist };
})();
